/**
 * usePasswordStrength
 *
 * Zero-dependency password strength analyser.
 * Returns a score 0-4 and a set of rule results that drive the UI.
 * No state — pure derived values from the input string.
 *
 * Extension point: add rules to RULES array without touching callers.
 */

export type StrengthLevel = 0 | 1 | 2 | 3 | 4;

export interface PasswordRule {
  key: string;
  label: string;
  pass: boolean;
}

export interface PasswordStrength {
  score: StrengthLevel;      // 0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong
  label: string;
  color: string;             // maps to CSS variable name (--correct, --incorrect, etc.)
  rules: PasswordRule[];
  isAcceptable: boolean;     // score >= 3
}

const RULES: Array<{ key: string; label: string; test: (p: string) => boolean }> = [
  {
    key: 'length',
    label: 'At least 8 characters',
    test: (p) => p.length >= 8,
  },
  {
    key: 'uppercase',
    label: 'One uppercase letter (A-Z)',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: 'lowercase',
    label: 'One lowercase letter (a-z)',
    test: (p) => /[a-z]/.test(p),
  },
  {
    key: 'digit',
    label: 'One number (0-9)',
    test: (p) => /\d/.test(p),
  },
  {
    key: 'special',
    label: 'One special character (!@#$…)',
    test: (p) => /[^A-Za-z0-9]/.test(p),
  },
];

const LEVELS: Record<StrengthLevel, { label: string; color: string }> = {
  0: { label: '',       color: 'var(--border)'    },
  1: { label: 'weak',   color: 'var(--incorrect)' },
  2: { label: 'fair',   color: '#f0a500'           },
  3: { label: 'good',   color: 'var(--accent)'    },
  4: { label: 'strong', color: 'var(--correct)'   },
};

export function getPasswordStrength(password: string): PasswordStrength {
  if (password.length === 0) {
    return {
      score: 0,
      label: '',
      color: LEVELS[0].color,
      rules: RULES.map((r) => ({ key: r.key, label: r.label, pass: false })),
      isAcceptable: false,
    };
  }

  const rules: PasswordRule[] = RULES.map((r) => ({
    key: r.key,
    label: r.label,
    pass: r.test(password),
  }));

  const passing = rules.filter((r) => r.pass).length;

  // Score: 0 empty, 1 = 1 rule, 2 = 2-3 rules, 3 = 4 rules, 4 = all 5 rules
  let score: StrengthLevel;
  if (passing === 0)      score = 1;
  else if (passing <= 2)  score = 1;
  else if (passing <= 3)  score = 2;
  else if (passing === 4) score = 3;
  else                    score = 4;

  return {
    score,
    label: LEVELS[score].label,
    color: LEVELS[score].color,
    rules,
    isAcceptable: score >= 3,
  };
}

// Hook wrapper — kept separate so the pure function can be tested without React
import { useMemo } from 'react';

export function usePasswordStrength(password: string): PasswordStrength {
  return useMemo(() => getPasswordStrength(password), [password]);
}
